import React, { useImperativeHandle, forwardRef, useRef, useEffect, useState } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import TapIcon from '../../services/TapIcon';
import Loader from '../../components/ui/Loader/Loader';
import { DisplayListPagination, DisplaySearchCriteria } from '../../components/TapUi';
import ApplicationLayout from '../../layouts/ApplicationLayout';
import PasscodelogSearchModal from './PasscodelogSearchModal';
import { ORG_API_BASE_URL_2 } from '../../services/TapApiUrls';
import { useSelector } from 'react-redux';
import DateService from '../../services/DateService';

const PasscodeLogList = forwardRef((props, ref) => {
    const searchpasscodeModalRef = useRef(null);
    const criteriaRef = useRef();
    const access_token = useSelector(state => state.auth.access_token);
    let listUrl = ORG_API_BASE_URL_2 + "/log/passcode/list";

    let initListData = {
        items: [],
        meta: null,
        loading: false,
        page: 1,
        per_page: 50
    };

    const [formSearchedElems, setFormSearchedElems] = useState([]);
    const [submittedSearchForm, setSubmittedSearchForm] = useState({});
    const [itemData, setItemData] = useState({ ...initListData });
    const [criteriaHeight, setCriteriaHeight] = useState(0);
    const [passcodes, setPasscodes] = useState([]);
    const [assets, setAssets] = useState([]);

    const updateCriteriaHeight = () => {
        if (criteriaRef.current) {
            const height = criteriaRef.current.offsetHeight;
            if (criteriaHeight !== height) {
                setCriteriaHeight(height);
            }
        }
    };

    useEffect(() => {
        updateCriteriaHeight();
        loadListData(1);
    }, [submittedSearchForm]);

    useEffect(() => {
        const handleResize = () => {
            updateCriteriaHeight();
        };
        const resizeObserver = new ResizeObserver(handleResize);
        if (criteriaRef.current) {
            resizeObserver.observe(criteriaRef.current);
        }
        updateCriteriaHeight();
        return () => {
            resizeObserver.disconnect();
        };
    }, [formSearchedElems]);

    const loadListData = async (page = 1, searchParams = null) => {
        setItemData(pd => ({ ...pd, loading: true, page: page }));
        try {
            const headers = { 'Authorization': 'Bearer ' + access_token };
            let params = {
                page: page,
                ...(searchParams !== null ? searchParams : submittedSearchForm),
            };
            const response = await api.server2Api.get(listUrl, {
                params, headers
            });
            let respData = response.data;
            if (respData.passcodes) {
                setPasscodes(respData.passcodes);
            }
            if (respData.assets) {
                setAssets(respData.assets);
            }

            let meta = {
                from: respData.data.from,
                to: respData.data.to,
                total: respData.data.total,
                last_page: respData.data.last_page,
                per_page: respData.data.per_page,
                current_page: respData.data.current_page
            };
            setItemData(pd => ({
                ...pd,
                items: respData.data.data || [],
                meta: meta
            }));
        } catch (error) {
            toast.error('Failed to load Passcode Log Listing');
        } finally {
            setItemData(pd => ({ ...pd, loading: false }));
        }
    };

    const searchHandle = (element, data) => {
        setFormSearchedElems(element);
        setSubmittedSearchForm(data);
    };

    const searchFormClear = () => {
        setFormSearchedElems([]);
        setSubmittedSearchForm({});
        if (searchpasscodeModalRef.current) {
            searchpasscodeModalRef.current.resetModalFilters();
        }
        loadListData(1, {});
    };

    const searchModalHandle = () => {
        if (searchpasscodeModalRef.current) {
            searchpasscodeModalRef.current.initModalHandler();
        }
    };

    const getPasscodeById = (id) => {
        const passcode = passcodes.find(p => p.id === id);
        return passcode ? passcode.passcode : '-';
    };

    const getAssetById = (assetId) => {
        return assets.find(a => a.asset_id === assetId);
    };

    const getAssetDetails = (assetId) => {
        const asset = getAssetById(assetId);
        if (asset) {
            return {
                name: asset.name || '-',
                code: asset.asset_code || '-',
                type: asset.asset_type_name || '-',
                model: asset.model_name || '-',
                manufacturer: asset.manufacturer_name || '-',
                site: asset.site || '-',
                tapAssetId: asset.tap_asset_id || '-',
                matrixUniqueId: asset.asset_matrix_unique_id || '-'
            };
        }
        return null;
    };

    const headerJsx = () => {
        return (
            <>
                <Helmet>
                    <title>Passcode Logs</title>
                </Helmet>
                <div className="page_title m0 row">
                    <div className='col-sm-12'>
                        <div className='row'>
                            <div className="d-flex align-items-center justify-content-between">
                                <h3>Passcode Log</h3>
                                <div className='text-start d-flex align-items-center justify-content-end mt15 gap-1'>
                                    <button type="button" className="btn btn-secondary" disabled={itemData.loading} onClick={searchModalHandle}>
                                        <TapIcon.imageIcon icon={TapIcon.SearchIcon} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    const PasscodeLogListJsx = () => {
        return (
            <>
                <div ref={criteriaRef}
                    style={{
                        position: 'sticky',
                        top: 0,
                        background: 'white',
                    }}>
                    <DisplaySearchCriteria
                        searchedElems={formSearchedElems}
                        onClearFilter={searchFormClear}
                    />
                </div>
                <table className="table table-bordered align-middle bg-white">
                    <thead className="align-middle table-secondary" style={{
                        top: `${criteriaHeight}px`,
                        position: 'sticky',
                    }}>
                        <tr className='text-center'>
                            <th style={{ width: "5%" }}>S.No</th>
                            <th style={{ width: "15%" }}>Passcode</th>
                            <th style={{ width: "15%" }}>Mobile</th>
                            <th style={{ width: "15%" }}>Passcode For</th>
                            <th style={{ width: "25%" }}>Resource Details</th>
                            <th style={{ width: "15%" }}>Created At</th>
                        </tr>
                    </thead>
                    <tbody>
                        {itemData.loading ? (
                            <tr>
                                <td className='text-center' colSpan={6}>
                                    <Loader />
                                </td>
                            </tr>
                        ) : (
                            itemData.items && itemData.items.length > 0 ? (
                                itemData.items.map((item, index) => {
                                    const assetDetails = item.action === 'asset' && item.asset_id ? getAssetDetails(item.asset_id) : null;

                                    return (
                                        <tr key={item.id}>
                                            <td className='text-center'>{(itemData.meta?.from || 1) + index}</td>
                                            <td className='text-center'>
                                                {getPasscodeById(item.passcode_id)}
                                            </td>
                                            <td className='text-center'>{item.mobile || '-'}</td>

                                            <td className='text-center text-capitalize'>
                                                {item.action}
                                            </td>
                                            <td className='text-start'>
                                                {assetDetails ? (
                                                    <div className="asset-details">
                                                        {assetDetails?.name} ({assetDetails?.code || "-"})
                                                        <div className="mt-1 fs12">
                                                            <span className="text-muted">Asset Type :</span> {assetDetails.type || "-"}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </td>
                                            <td className='text-center'>
                                                {item.created_at ? DateService.dateTimeFormat(item.created_at) : '-'}
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td className='text-center' colSpan={6}>
                                        <div className="py-4">
                                            <span className="text-muted">No records found</span>
                                        </div>
                                    </td>
                                </tr>
                            )
                        )}
                    </tbody>
                </table>
                <DisplayListPagination meta={itemData.meta} onPageChange={(e) => loadListData(e.selected + 1)} />
            </>
        );
    };

    return (
        <>
            <ApplicationLayout>
                <PasscodelogSearchModal
                    ref={searchpasscodeModalRef}
                    searchHandle={(element, searchData) => searchHandle(element, searchData)}
                    listingLoading={itemData.loading}
                    formSearchedElems={formSearchedElems}
                    appliedSearchData={submittedSearchForm}
                />
                {headerJsx()}
                <div className="container-fluid pl5">
                    <div className='page_containt row'>
                        <div className='col-sm-12 pageTbl'>
                            {PasscodeLogListJsx()}
                        </div>
                    </div>
                </div>
            </ApplicationLayout>
        </>
    );
});

export default PasscodeLogList;